import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { setDrawerOpen } from "../../utils/slices/ui";

/**
 * Pathname predicate for "focus-flow" routes that benefit from a
 * collapsed sidebar so the operator can read the surface edge-to-edge:
 * the per-asset chat, both demo review surfaces, the resolve-all page,
 * the orchestration board, the multi-market grid, and any open ticket
 * detail. We watch for these against the current `useRouterState`
 * pathname and auto-collapse the app sidebar; navigating back to a
 * non-focus surface auto-expands. Manual toggles between two routes in
 * the same category stick — `useAutoCollapseSidebar` only re-applies
 * an auto on category transitions, not on every render.
 */
export const FOCUS_PATH_FRAGMENTS = [
  "/asset-review",
  "/parallel-review",
  "/resolve-all",
  "/orchestration",
  "/multi-market",
  "/ask-puntt/", // chat detail (matches `/ask-puntt/<chatId>`); landing is `/ask-puntt` exactly
  "/tickets/", // ticket detail (matches `/tickets/<id>`); list is `/tickets` exactly
];

export function isFocusFlowPath(pathname: string): boolean {
  // The Ask Puntt projects launcher (`/ask-puntt/projects`) and project
  // workspaces (`/ask-puntt/projects/<id>`) are navigation / management
  // surfaces, not edge-to-edge focus surfaces — the sidebar (and its
  // new-project "+") must stay available there. The `/ask-puntt/` fragment
  // in FOCUS_PATH_FRAGMENTS is meant for chat detail (`/ask-puntt/<chatId>`)
  // only, but it also prefix-matches the projects routes, so exclude them
  // explicitly.
  if (/(^|\/)ask-puntt\/projects(\/|$|\?)/.test(pathname)) return false;

  for (const fragment of FOCUS_PATH_FRAGMENTS) {
    // Trailing slash means "subpath of"; bare segment means exact match.
    if (fragment.endsWith("/")) {
      if (pathname.startsWith(fragment)) return true;
    } else if (
      pathname === fragment ||
      pathname.startsWith(`${fragment}/`) ||
      pathname.startsWith(`${fragment}?`)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Auto-collapse the app sidebar on entering a focus-flow route, expand
 * back when leaving. Stores the last category we auto-applied; if the
 * operator manually flips the sidebar within the same category, that
 * choice sticks until they cross a category boundary.
 */
export function useAutoCollapseSidebar() {
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);
  const { location } = useRouterState();
  const lastCategoryRef = useRef<"focus" | "home" | null>(null);

  useEffect(() => {
    const category: "focus" | "home" = isFocusFlowPath(location.pathname)
      ? "focus"
      : "home";
    if (lastCategoryRef.current === category) return;
    lastCategoryRef.current = category;
    const desiredOpen = category !== "focus";
    if (sidebarOpen !== desiredOpen) {
      dispatch(setDrawerOpen({ app: desiredOpen }));
    }
    // Intentionally only react to pathname changes — manual toggles
    // mid-category are handled by `sidebarOpen` mismatching the desired
    // state, which we only correct at category transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
}
